import { useEffect, useState } from 'react';
import { Calculator } from 'lucide-react';
import { apiService } from '../services/api.service';

function money(n?: number | null) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
}

function fmtDate(d?: string | Date | null) {
  if (!d) return '—';
  const iso = typeof d === 'string' ? d.slice(0, 10) : d.toISOString().slice(0, 10);
  const [y, m, day] = iso.split('-');
  return `${day}/${m}/${y}`;
}

function apiErrorMessage(e: unknown): string {
  const err = e as { response?: { data?: { message?: string | string[] } }; message?: string };
  const msg = err?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join(', ');
  if (typeof msg === 'string') return msg;
  return err?.message ?? 'Error al simular';
}

type Props = {
  branchId: string;
  branches: Array<{ id: string; name: string }>;
};

export function DashboardLoanSimulator({ branchId, branches }: Props) {
  const [products, setProducts] = useState<any[]>([]);
  const [productId, setProductId] = useState('');
  const [amount, setAmount] = useState('');
  const [termMonths, setTermMonths] = useState('');
  const [simBranchId, setSimBranchId] = useState(branchId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [simulation, setSimulation] = useState<any | null>(null);

  useEffect(() => {
    setSimBranchId(branchId);
  }, [branchId]);

  useEffect(() => {
    let cancelled = false;
    apiService
      .getLoanProducts({ activeOnly: true, branchId: branchId || undefined })
      .then((list) => {
        if (cancelled) return;
        setProducts(list);
        setProductId((prev) => (prev && list.some((p: any) => p.id === prev) ? prev : ''));
      })
      .catch(() => {
        if (!cancelled) setProducts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [branchId]);

  const run = async () => {
    if (!productId || !amount || !termMonths) {
      setError('Elegí plan, monto y cantidad de cuotas');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await apiService.simulateLoanProduct(productId, {
        amount: parseFloat(amount),
        termMonths: parseInt(termMonths, 10),
        branchId: simBranchId || branchId || undefined,
      });
      setSimulation(result);
    } catch (err) {
      setSimulation(null);
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const needsBranchPick = !branchId && branches.length > 1;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Calculator className="h-4 w-4 text-slate-600" />
        <h2 className="text-sm font-semibold text-slate-900">Simulador de cuotas</h2>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Plan</label>
          <select
            value={productId}
            onChange={(e) => {
              setProductId(e.target.value);
              setSimulation(null);
            }}
            className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 text-sm"
          >
            <option value="">Seleccionar plan</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.planCode ? ` (${p.planCode})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Monto</label>
            <input
              type="number"
              min={0}
              step="100"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setSimulation(null);
              }}
              placeholder="0"
              className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Cuotas</label>
            <input
              type="number"
              min={1}
              step="1"
              value={termMonths}
              onChange={(e) => {
                setTermMonths(e.target.value);
                setSimulation(null);
              }}
              placeholder="12"
              className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm"
            />
          </div>
        </div>
        {needsBranchPick && (
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">
              Sucursal (opcional)
            </label>
            <select
              value={simBranchId}
              onChange={(e) => setSimBranchId(e.target.value)}
              className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 text-sm"
            >
              <option value="">Sin filtrar</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <button
          type="button"
          disabled={busy || !productId}
          onClick={run}
          className="w-full h-9 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? 'Calculando…' : 'Calcular'}
        </button>
        {error && <p className="text-xs text-rose-600">{error}</p>}
        {simulation?.schedule?.length > 0 && (
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <div className="max-h-60 overflow-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-500">
                    <th className="px-2 py-1.5 font-medium">Cuota</th>
                    <th className="px-2 py-1.5 font-medium">Venc.</th>
                    <th className="px-2 py-1.5 font-medium text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {simulation.schedule.map((row: any) => (
                    <tr key={row.installmentNumber} className="border-t border-slate-100">
                      <td className="px-2 py-1 tabular-nums">{row.installmentNumber}</td>
                      <td className="px-2 py-1">{fmtDate(row.dueDate)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{money(row.totalAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-slate-50 border-t border-slate-200 px-3 py-2 text-xs space-y-1">
              <p className="flex justify-between gap-2">
                <span className="text-slate-500">Cuota aprox.</span>
                <strong className="tabular-nums text-slate-900">{money(simulation.pmt)}</strong>
              </p>
              <p className="flex justify-between gap-2">
                <span className="text-slate-500">Total a pagar</span>
                <strong className="tabular-nums text-slate-900">{money(simulation.totalAmount)}</strong>
              </p>
              <p className="flex justify-between gap-2">
                <span className="text-slate-500">Cancelación</span>
                <strong className="tabular-nums text-slate-900">{fmtDate(simulation.maturityDate)}</strong>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
